import * as React from "react";
import { Check, X, Loader2, Calendar as CalendarIcon } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";

import { useGetAllQuery } from "@/store/studentApi";
import {
  useSaveAttendenceMutation,
  useGetAllAttendQuery,
  useUpdateAttendenceMutation,
} from "../store/attendenceApi";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AttendanceRecord, RootState, Student } from "@/types";

const AttendanceTable = () => {
  const { user } = useSelector((state: RootState) => state.user);
  const [saveAttendanceFn, { isLoading: isSaving }] =
    useSaveAttendenceMutation();
  const [updateAttendance, { isLoading: isUpdating }] =
    useUpdateAttendenceMutation();
  const { data: students } = useGetAllQuery(user?.token);
  const { data: allAttendance } = useGetAllAttendQuery(user?.token);

  const { id: paramsBCode } = useParams();
  const [date, setDate] = React.useState<Date>(new Date());
  const [existingAttendanceIds, setExistingAttendanceIds] = React.useState<
    string[]
  >([]);

  const [attendance, setAttendance] = React.useState<Record<string, boolean>>(
    {},
  );

  // Initialize attendance state when date or allAttendance changes
  React.useEffect(() => {
    if (allAttendance && students) {
      const selectedDate = format(date, "yyyy-MM-dd");
      const existingIds: string[] = [];

      const initialAttendance = students
        ?.filter((std: Student) => std.batch === paramsBCode)
        .reduce((acc: Record<string, boolean>, student: Student) => {
          const dateAttendance = allAttendance.find(
            (atd: AttendanceRecord) =>
              atd.student === student._id &&
              format(new Date(atd.date), "yyyy-MM-dd") === selectedDate,
          );

          if (dateAttendance) {
            existingIds.push(dateAttendance._id);
          }

          acc[student._id] = dateAttendance ? dateAttendance.attendence : false;
          return acc;
        }, {});

      setExistingAttendanceIds(existingIds);
      setAttendance(initialAttendance);
    }
  }, [allAttendance, students, paramsBCode, date]);

  const filteredStudents = students?.filter(
    (std: Student) => std.batch === paramsBCode,
  );

  const toggleAttendance = (studentId: string) => {
    setAttendance((prev) => ({
      ...prev,
      [studentId]: !prev[studentId],
    }));
  };

  const handleSave = async () => {
    const attendanceData = filteredStudents?.map((student: Student) => ({
      _id: existingAttendanceIds.find((id) => {
        const record = allAttendance?.find((atd) => atd._id === id);
        return record?.student === student._id;
      }),
      batchCode: student.batch,
      student: student._id,
      studentName: `${student.fname} ${student.lname}`,
      attendence: attendance[student._id] || false,
      date: date.toISOString(),
      year: date.getFullYear(),
      month: date.getMonth() + 1,
    }));

    try {
      if (existingAttendanceIds.length > 0) {
        // If we have existing attendance records, update them
        await updateAttendance({
          Token: user?.token,
          data: attendanceData.map((record) => ({
            ...record,
            _id: record._id as string, // Include the _id for updating
          })),
        }).unwrap();
        toast.success("Attendance Updated Successfully");
      } else {
        await saveAttendanceFn({
          Token: user?.token,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          data: attendanceData.map(({ _id, ...record }) => record), // Remove _id for new records
        }).unwrap();
        toast.success("Attendance Added Successfully");
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      toast.error("Failed to save attendance");
    }
  };

  const isLoading = isSaving || isUpdating;

  return (
    <Card className="w-full max-w-6xl mx-auto">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="text-2xl font-bold">
          <h1>Attendance Sheet</h1>
        </CardTitle>
        <div className="flex items-center space-x-4">
          <span className="text-sm text-muted-foreground">
            Total Students: {filteredStudents?.length || 0}
          </span>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-fit">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {format(date, "PPP")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(date) => date && setDate(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          {user?.role !== "admin" && (
            <Button
              onClick={handleSave}
              className="bg-red-600 hover:bg-red-700 transition-all duration-200"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {existingAttendanceIds.length > 0
                    ? "Updating..."
                    : "Saving..."}
                </>
              ) : existingAttendanceIds.length > 0 ? (
                "Update Attendance"
              ) : (
                "Save Attendance"
              )}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border overflow-auto">
          <table className="w-full border-collapse">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">
                  No.
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold">
                  Batch
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold">
                  Course
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold w-24">
                  Attendance
                </th>
              </tr>
            </thead>
            <tbody className="divide-y  bg-background text-foreground">
              {filteredStudents?.map((student: Student, index: number) => (
                <tr
                  key={student._id}
                  className="group hover:bg-muted transition-colors duration-200"
                >
                  <td className="px-4 py-3 text-sm font-medium text-foreground">
                    {index + 1}
                  </td>
                  <Link to={"/attendancehistory/" + student._id}>
                    <td className="px-4 py-3">
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {student.fname} {student.lname}
                        </div>
                        <div className="text-sm text-gray-500">
                          {student.email}
                        </div>
                      </div>
                    </td>
                  </Link>
                  <td className="px-4 py-3 text-sm text-foreground">
                    {student.batch}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">
                    {student.course}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Button
                      variant="ghost"
                      className={`w-10 h-10 p-0 transition-all duration-300 transform hover:scale-110 ${
                        attendance[student._id]
                          ? "text-red-600 hover:text-red-700"
                          : "text-gray-400 hover:text-gray-500"
                      }`}
                      onClick={() => toggleAttendance(student._id)}
                    >
                      {attendance[student._id] ? (
                        <Check className="w-6 h-6 animate-in zoom-in duration-200" />
                      ) : (
                        <X className="w-6 h-6" />
                      )}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default AttendanceTable;
